import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { patientService, extractPatients } from '../services/patientService'
import { Patient } from '../types/patient'
import { formatPatientName } from '../utils/name'
import { useLetterWriting } from '../contexts/LetterWritingContext'
import './PatientSelectorModal.css'

interface PatientSelectorModalProps {
  templateId: string
  onClose: () => void
  onSuccess?: () => void
}

export function PatientSelectorModal({
  templateId,
  onClose,
  onSuccess,
}: PatientSelectorModalProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const { openPanel: openLetterPanel } = useLetterWriting()

  const { data: patientsData, isLoading } = useQuery({
    queryKey: ['patients'],
    queryFn: async () => {
      const result = await patientService.getAll()
      return extractPatients(result)
    },
  })

  const filteredPatients = patientsData?.filter((patient: Patient) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      patient.firstName.toLowerCase().includes(query) ||
      patient.lastName.toLowerCase().includes(query) ||
      patient.patientNumber?.toLowerCase().includes(query) ||
      `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(query)
    )
  })

  const handlePatientSelect = (patientId: string) => {
    openLetterPanel({ patientId, templateId })
    onSuccess?.()
    onClose()
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content patient-selector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Select Patient</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="patient-selector-search">
            <label htmlFor="patient-search">Search Patients</label>
            <input
              id="patient-search"
              type="text"
              placeholder="Search by name or patient number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control"
              autoFocus
            />
          </div>

          {isLoading ? (
            <div className="patient-selector-loading">
              <p>Loading patients...</p>
            </div>
          ) : !filteredPatients || filteredPatients.length === 0 ? (
            <div className="patient-selector-empty">
              <p>No patients found.</p>
              {searchQuery && <p className="hint">Try adjusting your search query.</p>}
            </div>
          ) : (
            <div className="patient-selector-list">
              {filteredPatients.map((patient: Patient) => (
                <div
                  key={patient.id}
                  className="patient-selector-item"
                  onClick={() => handlePatientSelect(patient.id)}
                >
                  <div className="patient-item-header">
                    <h3>{formatPatientName(patient as any)}</h3>
                    {patient.patientNumber && (
                      <span className="patient-number">#{patient.patientNumber}</span>
                    )}
                  </div>
                  <div className="patient-item-details">
                    {patient.dateOfBirth && (
                      <span className="patient-detail">
                        DOB: {new Date(patient.dateOfBirth).toLocaleDateString()}
                      </span>
                    )}
                    {patient.gender && (
                      <span className="patient-detail">{patient.gender}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}













